'use client'

import { useEffect, useMemo, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { ThemeProvider as HabitpackInternalThemeProvider, useTheme } from '@/state/theme'
import { useAppSession, useAccessibility, updateAppSessionState } from '@/state/session/context'
import { getThemeById, getThemeVars } from '@/lib/themes'
import { invalidatePreferencesDomain } from '@/lib/query/shell-cache'
import { useUserThemeApi } from '@/features/emporium/hooks/use-user-theme-api'
import { Toaster } from 'sonner'
import { areSetsEqual } from '@/lib/utils'


function ThemeSessionSync() {
  const { session } = useAppSession()
  const queryClient = useQueryClient()
  const { activeTheme, syncOwnedThemesFromInventory, isThemeChanging } = useTheme()
  const { themeData, isFetched, setTheme } = useUserThemeApi(
    session.activeUserId,
    session.hydrated && Boolean(session.activeUserId),
  )

  const ownedThemesKey = useMemo(() => {
    const rawIds = themeData?.unlockedThemeIds ?? session.ownedEmporiumItemIds
    return [...rawIds].sort((a, b) => a.localeCompare(b)).join('|')
  }, [themeData?.unlockedThemeIds, session.ownedEmporiumItemIds])

  const lastSyncedThemeRef = useRef<string | null>(null)
  const lastHydrationRef = useRef<string | null>(null)

  useEffect(() => {
    if (!session.hydrated) return

    // 1. Handle Anonymous / Logout: Reset to protocol defaults
    if (!session.activeUserId) {
      if (lastHydrationRef.current !== 'anonymous') {
        syncOwnedThemesFromInventory(['core-protocol'], 'core-protocol')
        lastHydrationRef.current = 'anonymous'
        lastSyncedThemeRef.current = null
      }
      return
    }

    // 2. Data Preparation
    const serverThemeId = themeData?.activeThemeId ?? 'core-protocol'
    const serverThemeLabel = getThemeById(serverThemeId)?.name ?? serverThemeId
    const sessionThemeId = session.arsenal.activeThemeId ?? 'core-protocol'
    const sessionThemeLabel = session.arsenal.activeTheme ?? sessionThemeId

    // 3. Logic: RECONCILIATION PASS

    // STAGE A: Sync Server -> Session (Pull)
    // Runs when themeData arrives. We skip this if the user is actively changing themes
    // to prevent cached server data from reverting an in-flight optimistic UI change.
    if (themeData && !isThemeChanging) {
      const hasMissingOwned = themeData.unlockedThemeIds.some((id) => !session.ownedEmporiumItemIds.includes(id))
      const hasThemeMismatch = sessionThemeId !== serverThemeId || sessionThemeLabel !== serverThemeLabel

      if (hasMissingOwned || hasThemeMismatch) {
        updateAppSessionState((prev) => {
          const mergedOwnedIds = Array.from(new Set([...prev.ownedEmporiumItemIds, ...themeData.unlockedThemeIds]))
          if (
            prev.arsenal.activeThemeId === serverThemeId &&
            prev.arsenal.activeTheme === serverThemeLabel &&
            areSetsEqual(mergedOwnedIds, prev.ownedEmporiumItemIds)
          ) {
            return prev
          }

          return {
            ...prev,
            ownedEmporiumItemIds: mergedOwnedIds,
            arsenal: {
              ...prev.arsenal,
              activeThemeId: serverThemeId,
              activeTheme: serverThemeLabel,
            },
          }
        })
      }
    }

    // STAGE B: Sync Session/Server -> UI Hook (Bridge)
    // Injects the resolved inventory and active selection into the core theme context.
    const resolvedOwnedIds = themeData?.unlockedThemeIds ?? session.ownedEmporiumItemIds
    const preferredThemeId = themeData?.activeThemeId ?? sessionThemeId
    const hydrationKey = `${session.activeUserId}:${ownedThemesKey}:${preferredThemeId}`

    if (lastHydrationRef.current !== hydrationKey) {
      lastHydrationRef.current = hydrationKey
      syncOwnedThemesFromInventory(resolvedOwnedIds, preferredThemeId)
    }

    // STAGE C: Sync UI Hook -> Server (Push)
    // ONLY triggered by explicit user interaction (isThemeChanging).
    if (isFetched && themeData && isThemeChanging) {
      // If the UI state has already settled with the server state, mark as synced and stop.
      if (activeTheme.id === serverThemeId) {
        lastSyncedThemeRef.current = `${session.activeUserId}:${activeTheme.id}:${serverThemeId}`
        return
      }

      // HOLD: If the session storage hasn't caught up with the server yet, or the UI
      // hasn't settled with the session, wait to avoid pushing stale/default ID transitions.
      if (sessionThemeId !== serverThemeId || activeTheme.id !== sessionThemeId) return

      const syncKey = `${session.activeUserId}:${activeTheme.id}:${serverThemeId}`
      if (lastSyncedThemeRef.current !== syncKey) {
        lastSyncedThemeRef.current = syncKey
        void setTheme(activeTheme.id)
          .then(async () => {
            const nextThemeLabel = getThemeById(activeTheme.id)?.name ?? activeTheme.name
            updateAppSessionState((prev) => ({
              ...prev,
              arsenal: { ...prev.arsenal, activeThemeId: activeTheme.id, activeTheme: nextThemeLabel },
            }))
            await invalidatePreferencesDomain(queryClient, session.activeUserId)
          })
          .catch((err) => console.warn('theme preferences sync failed', err))
      }
    }
  }, [
    session.hydrated,
    session.activeUserId,
    session.arsenal.activeThemeId,
    session.arsenal.activeTheme,
    ownedThemesKey,
    themeData?.activeThemeId,
    isFetched,
    activeTheme.id,
    activeTheme.name,
    isThemeChanging,
    syncOwnedThemesFromInventory,
    setTheme,
    queryClient,
  ])

  return null
}

function ThemePreviewRouteGuard() {
  const pathname = usePathname()
  const { isPreviewActive, stopPreview } = useTheme()

  useEffect(() => {
    if (!isPreviewActive) return
    if (pathname === '/emporium/shop' || pathname === '/emporium/inventory') return
    stopPreview()
  }, [isPreviewActive, pathname, stopPreview])

  return null
}

function ReactiveToaster() {
  const { renderedTheme } = useTheme()
  
  // Heuristic: check background lightness from HSL components
  const vars = getThemeVars(renderedTheme)
  const bgHsl = vars['--background-hsl']
  
  let isLight = false
  if (bgHsl) {
    const parts = bgHsl.split(' ')
    const lPart = parts[parts.length - 1]
    const lValue = parseInt(lPart, 10)
    if (!isNaN(lValue) && lValue > 60) {
      isLight = true
    }
  } else {
    isLight = renderedTheme.id.includes('light') || 
              renderedTheme.id === 'paper' || 
              renderedTheme.id === 'zen-garden'
  }

  return (
    <Toaster 
      richColors 
      position="bottom-right" 
      closeButton 
      theme={isLight ? 'light' : 'dark'} 
    />
  )
}

function ThemeColorSync() {
  const { renderedTheme } = useTheme()

  useEffect(() => {
    // Read the background color from the computed style of document.body
    const bgColor = getComputedStyle(document.body).getPropertyValue('--background').trim()
    
    if (bgColor) {
      let meta = document.querySelector('meta[name="theme-color"]')
      if (!meta) {
        meta = document.createElement('meta')
        meta.setAttribute('name', 'theme-color')
        document.head.appendChild(meta)
      }
      meta.setAttribute('content', bgColor)
    }
  }, [renderedTheme])

  return null
}

export function HabitpackThemeProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const accessibility = useAccessibility()
  const highContrast = accessibility?.highContrast ?? false
  const reduceMotion = accessibility?.reduceMotion ?? false

  // Sync reduced motion state to body for Portals and global CSS overrides
  useEffect(() => {
    if (reduceMotion) {
      document.body.classList.add('force-reduce-motion')
    } else {
      document.body.classList.remove('force-reduce-motion')
    }
  }, [reduceMotion])

  return (
    <HabitpackInternalThemeProvider onPurchaseAttempt={() => true} highContrast={highContrast}>
      <ThemeSessionSync />
      <ThemeColorSync />
      <ThemePreviewRouteGuard />
      {children}
      <ReactiveToaster />
    </HabitpackInternalThemeProvider>
  )
}
